import { QueryResult, ResultStatus } from '@dispatch/shared/types';
import {
  QueryFunction,
  QueryKey,
  QueryStatus,
  useQuery as useReactQuery,
  UseQueryOptions,
} from 'react-query';

/**
 * Wraps react-query and maps to union status value
 * @param key
 * @param action
 * @returns { @see QueryResult }
 */
export function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  >
): QueryResult<TData, TError> {
  const { data, error, refetch, ...rest } = useReactQuery(
    queryKey,
    queryFn,
    options
  );

  return {
    data,
    error,
    refetch,
    isFetching: rest.isFetching,
    status: mapQueryToStatus(rest, data),
  };
}

export function mapQueryStatus<TData>(
  data: TData,
  status: QueryStatus,
  hasFilters: boolean,
  isFetchingNextPage: boolean,
  isFetchingPreviousPage: boolean
): ResultStatus {
  if (
    isFetchingNextPage ||
    isFetchingPreviousPage ||
    status === 'idle' ||
    status === 'loading'
  ) {
    return 'loading';
  }

  if (status === 'success') {
    if (Array.isArray(data) && data.length <= 0 && !hasFilters) {
      return 'empty';
    } else if (Array.isArray(data) && data.length <= 0 && hasFilters) {
      return 'noresult';
    }
    return 'success';
  }

  return 'error';
}

export function mapQueryToStatus<TData>(
  result: { isLoading: boolean; isError: boolean; isSuccess: boolean },
  data: TData
): ResultStatus {
  if (result.isLoading) {
    return 'loading';
  }

  if (result.isSuccess) {
    if (Array.isArray(data) && data.length <= 0) {
      return 'empty';
    }
    return 'success';
  }

  if (result.isError) {
    return 'error';
  }

  // Idle / disabled query (react-query `enabled: false`, never run) — NOT an
  // error. Treat as loading so a not-yet-enabled query (e.g. a bootstrap call
  // waiting on the access token / an in-progress auth flow) doesn't surface a
  // spurious error state — which previously flashed the BootstrapError screen
  // on the happy path before the queries enabled. See ADO #101210.
  return 'loading';
}
